import React from "react";
import { Box, Button, Card, Chip, Divider, Typography } from "@mui/material";

import { ReactComponent as Level1Icon } from "../../../resources/images/svg/home/profile/level1.svg";
import { ReactComponent as Level2Icon } from "../../../resources/images/svg/home/profile/level2.svg";
import { ReactComponent as Level3Icon } from "../../../resources/images/svg/home/profile/level3.svg";
import { ReactComponent as LevelOffIcon } from "../../../resources/images/svg/home/profile/levelOff.svg";
import GetALabel from "../../../resources/images/svg/home/getALabel.svg";
import AnnuairePro from "../../../resources/images/png/home/annuaire-pro.png";
import ContactPhoneTwoToneIcon from '@mui/icons-material/ContactPhoneTwoTone';
import { useIsMobile } from "../../../hooks/responsive";
import { useAuthToken } from "../../../hooks/auth";
import {
  useSetCertificateMutation,
} from "../../../store/api/user";
import moment from "moment";

const Level = ({ level }) => {
  return (
    <>
      <Typography
        sx={{
          color: "white",
          fontWeight: 900,
          fontSize: "18px",
          marginBottom: 1,
        }}
      >
        {level >= 3 ? "Bravo ! Vous êtes désormais praticien référencé." : "Palier d'engagement"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
        }}
      >
        <Level1Icon />
        {level >= 2 ? <Level2Icon /> : <LevelOffIcon />}
        {level >= 3 ? <Level3Icon /> : <LevelOffIcon />}
      </Box>
    </>
  );
};

const MilestoneElement = ({ level, userLevel }) => {
  const levelText = React.useMemo(() => {
    if (level <= 1) return "abonné";
    if (level === 2) return "engagé";
    if (level >= 3) return "référencé";
  }, [level]);

  const levelColor = React.useMemo(() => {
    if (level <= 1) return "#0596A1";
    if (level === 2) return "#3971F6";
    if (level >= 3) return "#9A45F4";
  }, [level]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 2,
        opacity: level < userLevel ? 0.5 : 1,
      }}
    >
      <Chip
        label={`${level}${level <= 1 ? "er" : "e"} palier`}
        sx={{
          color: "white",
          borderRadius: "4px",
          backgroundColor: levelColor,
          fontSize: "16px",
          padding: "6px",
          fontWeight: 900,
        }}
      />
      <Typography
        sx={{
          fontWeight: 900,
          color: levelColor,
          fontSize: "28px",
          wordWrap: "no-wrap",
        }}
      >{`Praticien ${levelText}`}</Typography>
    </Box>
  );
};

const MilestoneCard = ({ level, userLevel, userCertificatState }) => {
  const authToken = useAuthToken();
  const [currentCertificate, setCurrentCertificate] = React.useState({});

  React.useEffect(() => {
    if (userCertificatState) {
      setCurrentCertificate(userCertificatState);
    }
  }, [userCertificatState]);

  const levelColor = React.useMemo(() => {
    if (level <= 1) return "#0596A1";
    if (level === 2) return "#3971F6";
    if (level >= 3) return "#9A45F4";
  }, [level]);

  const LIST_STATUS_CERTIF = [
    { value: "pending", label: "En attente" },
    { value: "notReady", label: "Je ne suis pas encore prêt(e)" },
    { value: "applicant", label: "Je souhaite candidater" }
  ];


  const [setCertificateMutation] = useSetCertificateMutation();

  const handleValidate = async (newvalue) => {
    // 1. On gère le "skip" manuellement
    if (!authToken) {
      console.warn("Action annulée : Aucun token d'authentification.");
      return;
    }

    try {
      // 2. Succès : On ferme la modale
      setCurrentCertificate(prev => ({ ...prev, certificate: newvalue }));

      // 3. On déclenche la mutation
      // .unwrap() permet de tomber dans le catch en cas d'erreur (403, 504, etc.)
      await setCertificateMutation({
        authToken,
        certificate: newvalue,
      }).unwrap();


    } catch (err) {
      // 4. Gestion des erreurs Cloud Run
      console.error("Échec de la mutation :", err);
    }
  };

  return (
    <Card
      sx={{
        padding: 3,
        marginTop: 1,
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: levelColor + "22",
        alignItems: "flex-start",
        maxWidth: "500px",
        gap: 1,
      }}
    >
      <Typography
        sx={{
          color: "white",
          fontWeight: 900,
          fontSize: "18px",
          margin: 0,
        }}
      >
        Prochaine étape
      </Typography>
      <MilestoneElement level={level} userLevel={userLevel} />
      <Typography
        sx={{
          fontSize: "16px",
          color: "white",
          fontWeight: 900,
        }}
      >
        {level === 2
          ? (<>{`Découvrez et signez notre Charte éthique pour devenir praticien engagé.`}<br />
            {`Lors de la signature, indiquez vos prénom, nom et e-mail renseignés pour votre abonnement.`}<br />
            {`Notre service client validera votre dossier dans un délai de 48h après signature.`}</>)
          : (<>
            { currentCertificate?.certificate === "inProgress" ?
            `Votre demande a bien été enregistrée et est actuellement en cours d’étude.` :
            currentCertificate?.certificate === "rejected"  && moment(currentCertificate?.candidateDate).diff(moment(), 'days') >= 0 ?
            `Temps d'intégration recommandé – nouvelle candidature possible à partir 
            du ${moment(currentCertificate?.candidateDate).format("DD/MM/YYYY")}.` :
            (<Box
              sx={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                flexWrap: "wrap",
                marginTop: 2,
                gap: 2,
              }}
            >
              {`Candidature pour obtenir le label et intégrer l'annuaire - L'annuaire met en lumière des praticiens 
          engagés dans une démarche de qualité, de responsabilité et de confiance. Souhaitez-vous candidater ?`}

              {LIST_STATUS_CERTIF.map((certif) => {
                return (
                  <Chip
                    key={certif?.value}
                    label={certif?.label}
                    onClick={() => {
                      handleValidate(certif?.value);
                    }}
                    variant={currentCertificate?.certificate === certif.value ? "filled" : "outlined"}
                    sx={{
                      backgroundColor: `${currentCertificate?.certificate === certif.value ? "#3971F6" : "none"}`,
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "16px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: `${currentCertificate?.certificate === certif.value ? "#3971F6" : "#ffffff22 !important"}`,
                      },
                    }}
                  />
                );
              })}
            </Box>)}
            {currentCertificate?.certificate === "applicant" && (
              <>
                <br />
                {`La démarche de candidature vous donne accès à un formulaire à compléter. 
                  Toutes les informations nécessaires pour le remplir correctement sont indiquées 
                  en introduction.`}
                <br /><br />
                {`Prévoyez 45 minutes pour le compléter.`}<br /><br />
                {`Nous vous recommandons de préparer à l'avance les éléments suivants :`}<br /><br />
                {`- 2 témoignages audios (moins de 2 minutes chacun) de pairs répondant à 
                  la question « Pourquoi me recommanderiez-vous ? »`}<br /><br />
                {`- Au choix : un lien vers une page regroupant plusieurs avis clients, ou 2 témoignages 
                  audios (moins de 2 minutes chacun) de personnes accompagnées répondant à cette même 
                  question.`}
                <br /><br />
                <Button
                  onClick={() => {
                    window.open("https://form.typeform.com/to/yPn9rK8P", "_blank", "noopener,noreferrer");
                  }}
                  sx={{
                    marginTop: 1,
                    backgroundColor: levelColor,
                    color: "white",
                    borderRadius: "25px",
                    textTransform: "none",
                    width: "100%",
                    fontWeight: "bold"
                  }}
                >
                  {`Accéder au formulaire de candidature`}
                </Button>
              </>
            )}
          </>)}
      </Typography>
      {!(level > 2) && (<Button
        disabled={level > 2}
        onClick={() => {
          window.open("https://eu1.documents.adobe.com/public/esignWidget?wid=CBFCIBAA3AAABLblqZhAMvJ1RIIr34F-s1uAFk7RN5A-_VnhyXbFe4ALBajwlQP_cihgZEyzvK62wwqFh8ws", "_blank", "noopener,noreferrer");
        }}
        sx={{
          marginTop: 1,
          backgroundColor: levelColor,
          color: (theme) => theme.palette.getContrastText(levelColor),
          borderRadius: "25px",
          textTransform: "none",
          width: "100%",
        }}
      >
        {`Signer notre Charte éthique`}
      </Button>
      )}
    </Card>
  );
};

const Milestones = ({ userLevel = 1, userCertificatState = {certificate:'pending', candidateDate:'0000-00-00'} }) => {
  const levels = [1, 2, 3];
  return (
    <>
      <Typography
        sx={{
          color: "white",
          fontWeight: 900,
          fontSize: "18px",
          marginTop: 3,
          marginBottom: 1,
        }}
      >
        Vous êtes
      </Typography>
      {levels.map((level, index) => {
        const Component =
          level === userLevel + 1 ? MilestoneCard : MilestoneElement;
        return (
          <>
            <Component key={level} level={level} userLevel={userLevel} userCertificatState={userCertificatState} />
            {index < levels.length - 1 && (
              <Divider
                orientation="vertical"
                sx={{
                  borderColor: "lightgrey",
                  borderRadius: 1,
                  marginLeft: "50px",
                  marginRight: "auto",
                  height: "40px",
                  marginTop: 1,
                  marginBottom: 1,
                }}
              />
            )}
          </>
        );
      })}
    </>
  );
};


const Level3Infos = ({ userLevel = 1 }) => {
  const levels = [1, 2, 3];
  const isMobile = useIsMobile();
  const levelColor = "#9A45F4";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start"
      }}
    >
      <Box
        sx={{
          color: "white",
          fontWeight: 900,
          fontSize: "18px",
          marginBottom: isMobile ? 4 : 1,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Box
          sx={{
            justifyContent: "center",
            display: "inherit"
          }}
        >
          <img
            src={GetALabel}
            alt={"trusted label icon"}
            style={{
              maxWidth: "296px",
              marginLeft: isMobile ? 0 : "-40px"
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "26px",
              color: levelColor,
              fontWeight: 900,
              marginTop: isMobile ? "-25px" : "25px",
              textAlign: isMobile ? "center" : "none"
            }}
          >
            {`Votre label Inexploré`}
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              color: "white",
              fontWeight: 900,
            }}
          >
            {`Le label Inexploré renforce la crédibilité de votre pratique,
            et vous identifie comme membre d'un réseau de confiance.`}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          color: "white",
          fontWeight: 900,
          fontSize: "18px",
          marginBottom: 4,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <Box
          sx={{
            justifyContent: isMobile ? "center" : "start",
            display: "flex",
            alignItems: "start",
            minWidth: 120
          }}
        >

          <img
            src={AnnuairePro}
            alt={"Annuaire icon"}
            style={{
              maxWidth: "80px",
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "26px",
              color: levelColor,
              fontWeight: 900,
              textAlign: isMobile ? "center" : "none",
            }}
          >
            {`Visibilité dans l'annuaire`}
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              color: "white",
              fontWeight: 900,
            }}
          >
            {`Dès la fin de l'année 2026, bénéficiez d'une visibilité renforcée
            auprès du public en intégrant l'annuaire.`}
          </Typography>
        </Box>
      </Box>

      {/*<Box
        sx={{
          color: "white",
          fontWeight: 900,
          fontSize: "18px",
          marginBottom: 1,
        }}
      >
      
          <Typography
            sx={{
              fontSize: "16px",
              color: "white",
              fontWeight: 900,
            }}
          >
            {`Ne restez plus seul(e) dans la gestion de votre activité : 
            mutualisons nos forces et nos compétences !`}
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              color: "#0596A1",
              fontWeight: 900,
            }}
          >
            {`Si vous souhaitez bénéficier d'un accompagnement particulier,
            faites-le nous savoir. Nous vous recontacterons.`}
          </Typography>
      </Box>*/}
    </Box>
  );
};

const LevelSection = ({ proInfo }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        marginTop: 4,
      }}
    >
      <Level level={proInfo?.niveau ?? 1} />
      {proInfo?.niveau === 3 ? <Level3Infos userLevel={proInfo?.niveau ?? 1} /> : <Milestones userLevel={proInfo?.niveau ?? 1} userCertificatState={{candidateDate: proInfo?.candidateDate, certificate: proInfo?.certificate ?? 'pending'}} />}
    </Box>
  );
};

export default LevelSection;
